/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { ApiMultipleResponse } from 'models/ApiModels';
import { CreateUserPayload, EditUserPayload, RoleModel, UserParams, Users } from 'models/Users';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_COOKIE_MONSTER_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_COOKIE_MONSTER;

export const cookieMonsterApi = createApi({
  reducerPath: 'cookieMonsterApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const token = (getState() as ReduxState).app.accessToken;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
      }

      if (endpoint !== 'updateUserProfileImage') headers.set('Content-Type', 'application/json');

      return headers;
    }
  }),
  tagTypes: ['User', 'Users', 'Roles'],
  endpoints: (builder) => ({
    getUser: builder.query<Users, string>({
      query: (userId) => ({
        url: `/users/${userId}`
      }),
      providesTags: ['User']
    }),
    getUsers: builder.query<ApiMultipleResponse<Users>, UserParams>({
      query: (params) => ({
        url: `/users/`,
        params
      }),
      providesTags: ['Users']
    }),
    getRoles: builder.query<ApiMultipleResponse<RoleModel>, { includePermissions: boolean }>({
      query: (params) => ({
        url: `/roles/`,
        params
      }),
      providesTags: ['Roles']
    }),
    updateUser: builder.mutation<Users, { userId: string; payload: EditUserPayload }>({
      query: ({ userId, payload }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: payload
      }),
      invalidatesTags: ['User', 'Users']
    }),
    updateUserProfileImage: builder.mutation<Users, { userId: string; payload: FormData }>({
      query: ({ userId, payload }) => ({
        url: `/users/${userId}/profileImage`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['User', 'Users']
    }),
    createUser: builder.mutation<Users, { payload: CreateUserPayload }>({
      query: ({ payload }) => ({
        url: `/users/`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['User', 'Users']
    }),
    deleteUser: builder.mutation<Users, string>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Users']
    }),
    recoverUser: builder.mutation<Users, string>({
      query: (userId) => ({
        url: `/users/${userId}/recover`
      }),
      invalidatesTags: ['Users']
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useDeleteUserMutation,
  useRecoverUserMutation,
  useGetUserQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useGetRolesQuery,
  useUpdateUserProfileImageMutation,
  useGetApiNameQuery,
  useGetVersionQuery
} = cookieMonsterApi;
